"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

interface CountryResult {
  id: string;
  name: string;
  path: string;
  country_code: string;
}

/**
 * 백서 4.5/기술스펙 2.1: 리스팅의 region_id는 국가 단위까지 필수라, 가입 시점(기업 계정)에
 * 정확한 국가 하나를 반드시 골라야 한다 — 이전의 "한국/기타영어권/중국/EU" 4버킷 방식은
 * "기타영어권" 하나로 미국·영국·캐나다 등을 뭉뚱그려 실제 국가를 특정하지 못했다.
 */
export default function CountryPicker({ onSelect }: { onSelect: (country: CountryResult) => void }) {
  const t = useTranslations("countryPicker");
  const tc = useTranslations("common");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CountryResult[]>([]);
  const [selected, setSelected] = useState<CountryResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (query.trim().length === 0) {
        if (!cancelled) setResults([]);
        return;
      }
      const res = await fetch(`/api/regions/autocomplete?country_only=1&q=${encodeURIComponent(query)}`);
      const json = await res.json();
      if (!cancelled) setResults(json.results ?? []);
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  if (selected) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700">
        <span>{selected.name}</span>
        <button
          type="button"
          onClick={() => {
            setSelected(null);
            setQuery("");
          }}
          className="text-xs text-zinc-500 underline"
        >
          {tc("change")}
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-1">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("placeholder")}
        className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
      />
      {results.length > 0 && (
        <ul className="absolute top-full z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900">
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => {
                  setSelected(r);
                  onSelect(r);
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                {r.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
